import { Component, OnInit, computed, effect, inject, signal, viewChild } from '@angular/core';
import { FormField, form } from '@angular/forms/signals';
import { TranslatePipe } from '@ngx-translate/core';
import { Popover } from '@openng/optimus-ui/popover';
import { PrimeTemplate } from '@openng/optimus-ui/api';
import { IplpRecipeObject } from '@libs/types/src/list/object-type/iplp-recipe.object';
import { BaseDialogComponent } from '../../../../kit/base-dialog/base-dialog.component';
import { ActionButtonsComponent } from '../../../../kit/action-buttons/action-buttons.component';
import { ButtonComponent } from '../../../../kit/button/button.component';
import { TextInputComponent } from '../../../../kit/input/text-input-v2/text-input.component';
import { TableComponent } from '../../../../../modules/shared/components/kit/table/table.component';
import { TableColumn } from '../../../../../modules/shared/components/kit/table/table.types';
import { textColumn } from '../../../../../modules/shared/components/kit/table/table.fields';
import {
    FolderEPrescriptionMagistraliterRecipesDialogStore,
} from './folder-e-prescription-magistraliter-recipes-dialog.store';
import { LoadingComponent } from '../../../../kit/loading/loading.component';

@Component({
    selector: 'app-folder-e-prescription-magistraliter-recipes-dialog',
    templateUrl: './folder-e-prescription-magistraliter-recipes-dialog.component.html',
    styleUrl: './folder-e-prescription-magistraliter-recipes-dialog.component.scss',
    providers: [FolderEPrescriptionMagistraliterRecipesDialogStore],
    imports: [
        ActionButtonsComponent, ButtonComponent, TextInputComponent, TableComponent,
        FormField, TranslatePipe, Popover, PrimeTemplate, LoadingComponent,
    ],
})
export class FolderEPrescriptionMagistraliterRecipesDialogComponent extends BaseDialogComponent implements OnInit {
    protected readonly store = inject(FolderEPrescriptionMagistraliterRecipesDialogStore);

    private readonly details = viewChild<Popover>('details');
    private readonly table = viewChild<TableComponent>('recipesTable');

    protected readonly search = signal({ text: '' });
    protected readonly searchForm = form(this.search);
    protected readonly selected = signal<IplpRecipeObject | null>(null);
    protected readonly infoRecipe = signal<IplpRecipeObject | null>(null);
    protected readonly applying = signal(false);
    protected readonly columns: TableColumn[] = [
        textColumn('name', false, false),
        textColumn('formName', false, false),
        textColumn('indication', false, false),
        { ...textColumn('info', false, false), width: 50, exportAs: null },
    ];

    protected readonly recipes = computed(() => {
        const terms = this.normalize(this.search().text || '').split(/\s+/).filter(Boolean);
        return this.store.recipes().filter(recipe => {
            const text = this.normalize([recipe.name, recipe.formName, recipe.indication].join(' '));
            return terms.every(term => text.includes(term));
        });
    });

    constructor() {
        super();
        effect(() => {
            const selected = this.selected();
            if (selected && !this.recipes().some(recipe => recipe.id === selected.id)) {
                this.selected.set(null);
                this.table()?.selection.set(null);
            }
        });
    }

    public ngOnInit(): void {
        void this.store.load();
    }

    private normalize(value: string): string {
        return value.normalize('NFD').replace(/\p{Diacritic}/gu, '').toLocaleLowerCase('cs');
    }

    protected selectionChanged(selection: IplpRecipeObject | IplpRecipeObject[] | null): void {
        this.selected.set(Array.isArray(selection) ? selection[0] || null : selection);
    }

    protected showInfo(event: Event, recipe: IplpRecipeObject): void {
        event.stopPropagation();
        if (this.applying()) return;
        this.infoRecipe.set(recipe);
        this.details()?.show(event);
        void this.store.loadDetail(recipe.slug);
    }

    protected async useRecipe(): Promise<void> {
        const selected = this.selected();
        if (!selected || this.applying() || this.store.detailLoading()) return;
        this.details()?.hide();
        this.applying.set(true);
        const recipe = await this.store.loadDetail(selected.slug);
        this.applying.set(false);
        if (recipe) this.dialogRef.close(recipe);
    }
}
